import {ILogArguments} from "./ILogArguments";
import {localStorageService} from "../storage/local-storage";

/**
 * Takes log information and formats it to be highly readably
 * @param message The message to be logged. If this were an email, this would be the subject line.
 * @param showStack Whether or not to show the call stack.
 * @param data Additional data to be logged.
 * @param showDataOnOneLine Controls whether the data parameter information is displayed on one line or multiple lines.
 * @param additionalNote Additional information. If this were an email, this would be the body.
 * @param error An error object to be logged. Only used by the error method.
 */
export function getFormattedLogMessage({message, showStack, data, showDataOnOneLine, additionalNote, error}: ILogArguments): string {
  const formattedOptions = ensureOptionsHaveValues({
    message,
    showStack,
    data,
    showDataOnOneLine,
    additionalNote
  });

  const dateString = getTimestamp();

  let finalMessage = `${dateString} ${formattedOptions.message}`;

  if (formattedOptions.data) {
    const globalShowDataOnOneLine = getGlobalShowDataOnOneLine();
    if (globalShowDataOnOneLine === "true" || (globalShowDataOnOneLine === "deferred" && formattedOptions.showDataOnOneLine && formattedOptions.data.length)) {
      finalMessage += "(" + formattedOptions.data.map(dataPoint => `${dataPoint.key}: ${dataPoint.value}`).join(", ") + ")";
    } else {
      finalMessage += formattedOptions.data.map(dataPoint => `\n\t${dataPoint.key} ---> ${dataPoint.value}`).join("");
    }
  }

  if (formattedOptions.additionalNote && formattedOptions.additionalNote.length > 0) {
    finalMessage += `\n\tNOTE: ${formattedOptions.additionalNote}`;
  }

  if (formattedOptions.showStack) {
    const methodNames = getMethodNamesInStackTrace();

    if (methodNames.length) {
      const globalShowStackValue = getGlobalShowStackValue();

      if (globalShowStackValue === "true" || (formattedOptions.showStack && globalShowStackValue === "deferred")) {
        finalMessage += '\n\tCall stack:' + methodNames.join(' <-- ');
      }
    }
  }

  if (error) {
    finalMessage += '\n\tError: ' + error.stack;
  }

  return finalMessage;
}

function getMethodNamesInStackTrace(): string[] {
  const error = new Error();
  const stack = error.stack?.split('\n') || [];
  const methodNames = stack
    .slice(3) // Skip the first three lines (Error and printCallStack and callingMethodName)
    .map(line => line.trim().split(' ')[1]) // Extract method names
    .filter(name => name); // Remove undefined or empty names
  return methodNames;
}

function getGlobalShowDataOnOneLine(): "true" | "false" | "deferred" {
  const storedValue = getStringValue("logging_global_showDataOnOneLine", "deferred");
  if (["true", "false", "deferred"].includes(storedValue)) {
    const castValue = storedValue as "true" | "false" | "deferred";
    return castValue
  }
  return "deferred";
}

function getGlobalShowStackValue(): "true" | "false" | "deferred" {
  const storedValue = getStringValue("logging_global_showStacks", "deferred");
  if (["true", "false", "deferred"].includes(storedValue)) {
    const castValue = storedValue as "true" | "false" | "deferred";
    return castValue
  }
  return "deferred";
}

function getStringValue(key: string, defaultValue: string = ""): string {
  let result = localStorageService.getJsonItem<string>(key);

  if (result === undefined) {
    result = defaultValue;
    localStorageService.setJsonItem(key, result);
  }

  return result;
}

function getTimestamp(): string {
  const now = new Date();
  const dateString = ensureIs2Digits(now.getMonth() + 1)
    + "/" + ensureIs2Digits(now.getDate())
    + "/" + now.getFullYear()
    + " " + ensureIs2Digits(now.getHours())
    + ":" + ensureIs2Digits(now.getMinutes())
    + ":" + ensureIs2Digits(now.getSeconds())
    + "." + now.getMilliseconds().toString().padStart(3, '0');
  return dateString;
}

function ensureIs2Digits (num: number) {
  return num.toString().padStart(2, '0');
}

function ensureOptionsHaveValues(options: ILogArguments): ILogArguments {
  options = options ?? {};
  options.showStack = options.showStack ?? false;
  options.message = options.message ?? "";
  options.data = options.data ?? [];
  options.showDataOnOneLine = options.showDataOnOneLine ?? true;
  options.additionalNote = options.additionalNote ?? "";

  return options;
}